import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { requestSendEmailCode } from '@/services/docs-site-auth/requestSendEmailCode';
import { requestVerifyEmailCode } from '@/services/docs-site-auth/requestVerifyEmailCode';
import type { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import InnerAsyncMarkdownViewer from '@/ui-kit/components/MarkdownSafeMdxViewer/viewer';
import type { DocsSiteVisibilitySettings } from '@tarslib/types/lib/publishedProject';
import { Button, Form, Input, message, Spin } from 'aui';
import clsx from 'clsx';
import { useState } from 'react';
import CaptchaFormItem from './Captcha';
import styles from './DocsSiteAuth.module.css';

export enum EmailSettingsSubtitleType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

export enum EmailWhitelistMessageType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

const EmailValidator = (props: {
  id: number | string;
  imgRender: () => JSX.Element;
  name?: string;
  projectId: number;
  subdirectory?: string;
  visitType?: DocsSiteVisitType;
  visibilitySettings: DocsSiteVisibilitySettings;
}) => {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const [emailForm] = Form.useForm();
  const [sendEmailCodeLoading, setSendEmailCodeLoading] = useState<boolean>(false);
  const envConfig = useEnvConfigModelStore((x) => x);
  const appLowerName = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const { id, imgRender, name, projectId, subdirectory, visitType, visibilitySettings } = props;
  const emailSettings = visibilitySettings.advancedSettings?.emailSettings;
  const subtitleType = emailSettings?.subtitleType;
  const customSubtitle = emailSettings?.subtitle;
  const emailInWhitelistMessageType = emailSettings?.emailInWhitelistMessageType;
  const emailNotInWhitelistMessageType = emailSettings?.emailNotInWhitelistMessageType;
  const emailInWhitelistMessage = emailSettings?.emailInWhitelistMessage;
  const emailNotInWhitelistMessage = emailSettings?.emailNotInWhitelistMessage;

  const verifyEmailCodeAndNav = async (email: string, code: string) => {
    const [error] = await requestVerifyEmailCode(
      {
        id,
        projectId,
        email,
        code,
      },
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (!error) {
      window.location.reload();
    }
  };

  const onSend = async (email: string) => {
    let result = false;
    setSendEmailCodeLoading(true);
    const [error] = await requestSendEmailCode(
      { id, projectId, email },
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (!error) {
      setSendEmailCodeLoading(false);
      result = true;
      if (emailInWhitelistMessageType === EmailWhitelistMessageType.CUSTOM) {
        message.info({
          content: (
            <InnerAsyncMarkdownViewer
              value={emailInWhitelistMessage}
              inDocView={false}
              renderExternalLink={(url: string) => {
                return url;
              }}
              appLowerName={appLowerName}
            />
          ),
          className: styles.emailValidator__customError,
          icon: <></>,
        });
      } else {
        message.success(clientI18n('onlineShare.sendCodeSuccess'));
      }
    } else {
      if (emailNotInWhitelistMessageType === EmailWhitelistMessageType.CUSTOM) {
        message.info({
          content: (
            <InnerAsyncMarkdownViewer
              value={emailNotInWhitelistMessage}
              inDocView={false}
              renderExternalLink={(url: string) => {
                return url;
              }}
              appLowerName={appLowerName}
            />
          ),
          className: styles.emailValidator__customError,
          icon: <></>,
        });
      } else {
        message.success(clientI18n('onlineShare.sendCodeSuccess'));
      }
    }
    return result;
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async ({ email, code }: { email: string; code: string }) => {
    setSubmitting(true);
    await verifyEmailCodeAndNav(email, code);
    setSubmitting(false);
  };

  const renderSubtitle = () => {
    if (subtitleType === EmailSettingsSubtitleType.CUSTOM) {
      return (
        <InnerAsyncMarkdownViewer
          value={customSubtitle}
          inDocView={false}
          renderExternalLink={(url: string) => {
            return url;
          }}
          appLowerName={appLowerName}
        />
      );
    }
    return <>{clientI18n('email.auth.tip')}</>;
  };

  return (
    <div className={clsx('flex flex-col items-center justify-center', styles.emailValidator)}>
      {imgRender()}
      <div className={styles.auth__name}>{name}</div>
      <div className={styles.auth__description}>{renderSubtitle()}</div>
      <div className={clsx(styles.auth__input__component, 'w-full')}>
        <Spin spinning={submitting}>
          <Form onFinish={handleSubmit} form={emailForm}>
            <Form.Item
              name="email"
              style={{ marginBottom: 16 }}
              validateTrigger={['onSubmit']}
              rules={[
                {
                  required: true,
                  message: clientI18n('user.loginOrRegister.rule.required.mail'),
                },
                {
                  type: 'email',
                  message: clientI18n('user.loginOrRegister.rule.pattern.mail'),
                },
              ]}
            >
              <Input
                size="large"
                allowClear
                placeholder={clientI18n('user.loginOrRegister.email')}
              />
            </Form.Item>
            <CaptchaFormItem
              inputProps={{ autoFocus: false }}
              sendTxt={clientI18n('user.loginOrRegister.captcha.sendEmail')}
              onStart={async () => {
                await emailForm.validateFields(['email']);
                const success = await onSend(emailForm.getFieldValue('email'));
                return success;
              }}
              loading={sendEmailCodeLoading}
            />
            <Button type="primary" block htmlType="submit" size="large" loading={submitting}>
              {clientI18n('view')}
            </Button>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default EmailValidator;
